<template>
  <div
    id="app-sidebar-9"
    class="h-screen bg-surface-0 dark:bg-surface-950 hidden lg:block flex-shrink-0 absolute lg:static left-0 top-0 z-10 border-r border-surface transition-all duration-300"
    :class="isCollapsed ? 'w-[52px]' : 'min-w-[220px]'"
  >
    <div class="flex flex-col h-full">
      <div class="flex items-center justify-center flex-shrink-0 bg-primary h-[60px] relative">
        <img
          src="~/assets/images/logo.svg"
          alt="Krewebacchus Logo"
          class="logo transition-all duration-300"
          :class="isCollapsed ? 'w-7 h-7' : 'w-9 h-9'"
        />
        <!-- Collapse toggle button -->
        <button
          class="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-surface-100 dark:bg-surface-800 border border-surface-300 dark:border-surface-600 rounded-full flex items-center justify-center hover:bg-surface-200 dark:hover:bg-surface-700 transition-colors z-20"
          :title="isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
          @click="toggleCollapse"
        >
          <i
            class="pi text-xs text-surface-600 dark:text-surface-300"
            :class="isCollapsed ? 'pi-chevron-right' : 'pi-chevron-left'"
          />
        </button>
      </div>

      <div
        class="mt-2 overflow-y-auto flex-1 min-h-0"
        :class="isCollapsed ? '' : 'pr-4'"
      >
        <ul class="list-none p-0 m-0">
          <!-- Regular member items -->
          <li>
            <a
              v-tooltip.right="!showLabels ? { value: 'Home', showOnlyWhenTruncated: false, class: 'tooltip-custom' } : null"
              class="flex flex-row items-center cursor-pointer p-4 border-l-2 duration-150 transition-colors"
              :class="[
                isActiveRoute('/')
                  ? 'text-primary-emphasis border-primary-emphasis'
                  : 'text-surface-600 dark:text-surface-200 border-transparent hover:border-surface-300 dark:hover:border-surface-500',
                showLabels ? 'justify-start' : 'justify-center',
              ]"
              @click="handleNavigation('/')"
            >
              <i
                class="pi pi-home !text-base lg:!text-xl !leading-none"
                :class="showLabels ? 'mr-3' : ''"
              />
              <span
                v-if="showLabels"
                class="font-medium text-base"
              >Home</span>
            </a>
          </li>

          <li>
            <a
              v-tooltip.right="!showLabels ? { value: props.hasSubkrewe ? 'My SubKrewe' : 'Find SubKrewe', showOnlyWhenTruncated: false, class: 'tooltip-custom' } : null"
              class="flex flex-row items-center cursor-pointer p-4 border-l-2 duration-150 transition-colors"
              :class="[
                isActiveRoute('/subkrewe')
                  ? 'text-primary-emphasis border-primary-emphasis'
                  : 'text-surface-600 dark:text-surface-200 border-transparent hover:border-surface-300 dark:hover:border-surface-500',
                showLabels ? 'justify-start' : 'justify-center',
              ]"
              @click="handleNavigation('/subkrewe')"
            >
              <Icon
                v-if="props.hasSubkrewe"
                name="icon-park-outline:umbrella"
                class="!text-base lg:!text-xl !leading-none"
                :class="showLabels ? 'mr-3' : ''"
              />
              <i
                v-else
                class="pi pi-search !text-base lg:!text-xl !leading-none"
                :class="showLabels ? 'mr-3' : ''"
              />
              <span
                v-if="showLabels"
                class="font-medium text-base"
              >{{ props.hasSubkrewe ? 'My SubKrewe' : 'Find SubKrewe' }}</span>
            </a>
          </li>

          <li>
            <a
              v-tooltip.right="!showLabels ? { value: 'My QR Code', showOnlyWhenTruncated: false, class: 'tooltip-custom' } : null"
              class="flex flex-row items-center cursor-pointer p-4 border-l-2 duration-150 transition-colors"
              :class="[
                isActiveRoute('/checkin/qrcode')
                  ? 'text-primary-emphasis border-primary-emphasis'
                  : 'text-surface-600 dark:text-surface-200 border-transparent hover:border-surface-300 dark:hover:border-surface-500',
                showLabels ? 'justify-start' : 'justify-center',
              ]"
              @click="handleNavigation('/checkin/qrcode')"
            >
              <i
                class="pi pi-qrcode !text-base lg:!text-xl !leading-none"
                :class="showLabels ? 'mr-3' : ''"
              />
              <span
                v-if="showLabels"
                class="font-medium text-base"
              >My QR Code</span>
            </a>
          </li>

          <li>
            <a
              v-tooltip.right="!showLabels ? { value: 'Settings', showOnlyWhenTruncated: false, class: 'tooltip-custom' } : null"
              class="flex flex-row items-center cursor-pointer p-4 border-l-2 duration-150 transition-colors"
              :class="[
                isActiveRoute('/settings')
                  ? 'text-primary-emphasis border-primary-emphasis'
                  : 'text-surface-600 dark:text-surface-200 border-transparent hover:border-surface-300 dark:hover:border-surface-500',
                showLabels ? 'justify-start' : 'justify-center',
              ]"
              @click="handleNavigation('/settings')"
            >
              <i
                class="pi pi-cog !text-base lg:!text-xl !leading-none"
                :class="showLabels ? 'mr-3' : ''"
              />
              <span
                v-if="showLabels"
                class="font-medium text-base"
              >Settings</span>
            </a>
          </li>

          <!-- Admin separator -->
          <li
            v-if="showAdminSection"
            class="px-4 py-2"
          >
            <div class="flex items-center gap-2">
              <hr class="flex-1 border-t border-surface-300 dark:border-surface-600" />
              <span
                v-if="showLabels"
                class="text-xs text-surface-500 dark:text-surface-400 uppercase tracking-wider"
              >Admin</span>
              <hr
                v-if="showLabels"
                class="flex-1 border-t border-surface-300 dark:border-surface-600"
              />
            </div>
          </li>

          <!-- Admin items -->
          <li v-if="isAdmin || isRedshirtAdmin">
            <a
              v-tooltip.right="!showLabels ? { value: (isRedshirtAdmin && !isAdmin) ? 'Manage Red Shirts' : 'Manage Members', showOnlyWhenTruncated: false, class: 'tooltip-custom' } : null"
              class="flex flex-row items-center cursor-pointer p-4 border-l-2 duration-150 transition-colors"
              :class="[
                isActiveRoute('/admin/members')
                  ? 'text-primary-emphasis border-primary-emphasis'
                  : 'text-surface-600 dark:text-surface-200 border-transparent hover:border-surface-300 dark:hover:border-surface-500',
                showLabels ? 'justify-start' : 'justify-center',
              ]"
              @click="handleNavigation('/admin/members')"
            >
              <i
                class="pi pi-users !text-base lg:!text-xl !leading-none"
                :class="showLabels ? 'mr-3' : ''"
              />
              <span
                v-if="showLabels"
                class="font-medium text-base"
              >{{ (isRedshirtAdmin && !isAdmin) ? 'Red Shirts' : 'Members' }}</span>
            </a>
          </li>

          <!-- Captains menu item for redshirt admins -->
          <li v-if="isRedshirtAdmin && !isAdmin">
            <a
              v-tooltip.right="!showLabels ? { value: 'View Captains', showOnlyWhenTruncated: false, class: 'tooltip-custom' } : null"
              class="flex flex-row items-center cursor-pointer p-4 border-l-2 duration-150 transition-colors"
              :class="[
                isActiveRoute('/admin/captains')
                  ? 'text-primary-emphasis border-primary-emphasis'
                  : 'text-surface-600 dark:text-surface-200 border-transparent hover:border-surface-300 dark:hover:border-surface-500',
                showLabels ? 'justify-start' : 'justify-center',
              ]"
              @click="handleNavigation('/admin/captains')"
            >
              <i
                class="pi pi-star !text-base lg:!text-xl !leading-none"
                :class="showLabels ? 'mr-3' : ''"
              />
              <span
                v-if="showLabels"
                class="font-medium text-base"
              >Captains</span>
            </a>
          </li>

          <li v-if="isAdmin || isBandAdmin || isRedshirtAdmin">
            <a
              v-tooltip.right="!showLabels ? { value: isBandAdmin && !isAdmin ? 'Manage Bands' : 'Manage SubKrewes', showOnlyWhenTruncated: false, class: 'tooltip-custom' } : null"
              class="flex flex-row items-center cursor-pointer p-4 border-l-2 duration-150 transition-colors"
              :class="[
                isActiveRoute('/admin/subkrewes')
                  ? 'text-primary-emphasis border-primary-emphasis'
                  : 'text-surface-600 dark:text-surface-200 border-transparent hover:border-surface-300 dark:hover:border-surface-500',
                showLabels ? 'justify-start' : 'justify-center',
              ]"
              @click="handleNavigation('/admin/subkrewes')"
            >
              <i
                class="pi pi-sitemap !text-base lg:!text-xl !leading-none"
                :class="showLabels ? 'mr-3' : ''"
              />
              <span
                v-if="showLabels"
                class="font-medium text-base"
              >{{ isBandAdmin && !isAdmin ? 'Bands' : 'SubKrewes' }}</span>
            </a>
          </li>

          <!-- Inventory Menu (admin and redshirt admin) -->
          <li v-if="isAdmin || isRedshirtAdmin">
            <div
              v-tooltip.right="!showLabels ? { value: 'Inventory', showOnlyWhenTruncated: false, class: 'tooltip-custom' } : null"
              class="flex flex-row items-center cursor-pointer p-4 border-l-2 duration-150 transition-colors"
              :class="[
                isInventoryActive
                  ? 'text-primary-emphasis border-primary-emphasis'
                  : 'text-surface-600 dark:text-surface-200 border-transparent hover:border-surface-300 dark:hover:border-surface-500',
                showLabels ? 'justify-start' : 'justify-center',
              ]"
              @click="showLabels ? (inventoryExpanded = !inventoryExpanded) : handleNavigation('/admin/inventory')"
            >
              <i
                class="pi pi-box !text-base lg:!text-xl !leading-none"
                :class="showLabels ? 'mr-3' : ''"
              />
              <span
                v-if="showLabels"
                class="font-medium text-base flex-1"
              >Inventory</span>
              <i
                v-if="showLabels"
                class="pi !text-xs !leading-none transition-transform"
                :class="inventoryExpanded ? 'pi-chevron-down' : 'pi-chevron-right'"
              />
            </div>

            <!-- Submenu items -->
            <ul
              v-if="inventoryExpanded && showLabels"
              class="list-none p-0 m-0 ml-4"
            >
              <li>
                <a
                  class="flex flex-row items-center cursor-pointer p-3 pl-8 border-l-2 duration-150 transition-colors text-sm"
                  :class="[
                    isActiveRoute('/admin/inventory')
                      ? 'text-primary-emphasis border-primary-emphasis'
                      : 'text-surface-600 dark:text-surface-200 border-transparent hover:border-surface-300 dark:hover:border-surface-500',
                  ]"
                  @click="handleNavigation('/admin/inventory')"
                >
                  <i class="pi pi-cog mr-2 !text-sm" />
                  <span class="font-medium">Management</span>
                </a>
              </li>
              <li>
                <a
                  class="flex flex-row items-center cursor-pointer p-3 pl-8 border-l-2 duration-150 transition-colors text-sm"
                  :class="[
                    isActiveRoute('/checkin/equipment')
                      ? 'text-primary-emphasis border-primary-emphasis'
                      : 'text-surface-600 dark:text-surface-200 border-transparent hover:border-surface-300 dark:hover:border-surface-500',
                  ]"
                  @click="handleNavigation('/checkin/equipment')"
                >
                  <i class="pi pi-box mr-2 !text-sm" />
                  <span class="font-medium">Equipment Station</span>
                </a>
              </li>
            </ul>
          </li>

          <!-- Lineup Menu with Submenus -->
          <li v-if="showLineupMenu && hasLineupSubmenu">
            <!-- Submenu for admins/lieutenants -->
            <div
              v-tooltip.right="!showLabels ? { value: 'Lineup', showOnlyWhenTruncated: false, class: 'tooltip-custom' } : null"
              class="flex flex-row items-center cursor-pointer p-4 border-l-2 duration-150 transition-colors"
              :class="[
                isLineupActive
                  ? 'text-primary-emphasis border-primary-emphasis'
                  : 'text-surface-600 dark:text-surface-200 border-transparent hover:border-surface-300 dark:hover:border-surface-500',
                showLabels ? 'justify-start' : 'justify-center',
              ]"
              @click="showLabels ? (lineupExpanded = !lineupExpanded) : handleNavigation('/lineup')"
            >
              <i
                class="pi pi-map-marker !text-base lg:!text-xl !leading-none"
                :class="showLabels ? 'mr-3' : ''"
              />
              <span
                v-if="showLabels"
                class="font-medium text-base flex-1"
              >Lineup</span>
              <i
                v-if="showLabels"
                class="pi !text-xs !leading-none transition-transform"
                :class="lineupExpanded ? 'pi-chevron-down' : 'pi-chevron-right'"
              />
            </div>

            <!-- Submenu items -->
            <ul
              v-if="lineupExpanded && showLabels"
              class="list-none p-0 m-0 ml-4"
            >
              <li v-if="canManageLineup">
                <a
                  class="flex flex-row items-center cursor-pointer p-3 pl-8 border-l-2 duration-150 transition-colors text-sm"
                  :class="[
                    isActiveRoute('/admin/lineup')
                      ? 'text-primary-emphasis border-primary-emphasis'
                      : 'text-surface-600 dark:text-surface-200 border-transparent hover:border-surface-300 dark:hover:border-surface-500',
                  ]"
                  @click="handleNavigation('/admin/lineup')"
                >
                  <i class="pi pi-cog mr-2 !text-sm" />
                  <span class="font-medium">Manage</span>
                </a>
              </li>
              <li v-if="canViewLineup">
                <a
                  class="flex flex-row items-center cursor-pointer p-3 pl-8 border-l-2 duration-150 transition-colors text-sm"
                  :class="[
                    isActiveRoute('/lineup')
                      ? 'text-primary-emphasis border-primary-emphasis'
                      : 'text-surface-600 dark:text-surface-200 border-transparent hover:border-surface-300 dark:hover:border-surface-500',
                  ]"
                  @click="handleNavigation('/lineup')"
                >
                  <i class="pi pi-eye mr-2 !text-sm" />
                  <span class="font-medium">View</span>
                </a>
              </li>
              <li v-if="canCheckInLineup">
                <a
                  class="flex flex-row items-center cursor-pointer p-3 pl-8 border-l-2 duration-150 transition-colors text-sm"
                  :class="[
                    isActiveRoute('/admin/lineup-status')
                      ? 'text-primary-emphasis border-primary-emphasis'
                      : 'text-surface-600 dark:text-surface-200 border-transparent hover:border-surface-300 dark:hover:border-surface-500',
                  ]"
                  @click="handleNavigation('/admin/lineup-status')"
                >
                  <i class="pi pi-check-circle mr-2 !text-sm" />
                  <span class="font-medium">Check In</span>
                </a>
              </li>
              <li v-if="canViewRadioList">
                <a
                  class="flex flex-row items-center cursor-pointer p-3 pl-8 border-l-2 duration-150 transition-colors text-sm"
                  :class="[
                    isActiveRoute('/admin/radio-list')
                      ? 'text-primary-emphasis border-primary-emphasis'
                      : 'text-surface-600 dark:text-surface-200 border-transparent hover:border-surface-300 dark:hover:border-surface-500',
                  ]"
                  @click="handleNavigation('/admin/radio-list')"
                >
                  <i class="pi pi-megaphone mr-2 !text-sm" />
                  <span class="font-medium">Radio List</span>
                </a>
              </li>
            </ul>
          </li>

          <!-- Reporting Menu with Submenus (includes Emails) -->
          <li v-if="isAdmin">
            <div
              v-tooltip.right="!showLabels ? { value: 'Reporting', showOnlyWhenTruncated: false, class: 'tooltip-custom' } : null"
              class="flex flex-row items-center cursor-pointer p-4 border-l-2 duration-150 transition-colors"
              :class="[
                isReportingActive
                  ? 'text-primary-emphasis border-primary-emphasis'
                  : 'text-surface-600 dark:text-surface-200 border-transparent hover:border-surface-300 dark:hover:border-surface-500',
                showLabels ? 'justify-start' : 'justify-center',
              ]"
              @click="showLabels ? (reportingExpanded = !reportingExpanded) : handleNavigation('/admin/emails')"
            >
              <i
                class="pi pi-file-export !text-base lg:!text-xl !leading-none"
                :class="showLabels ? 'mr-3' : ''"
              />
              <span
                v-if="showLabels"
                class="font-medium text-base flex-1"
              >Reporting</span>
              <i
                v-if="showLabels"
                class="pi !text-xs !leading-none transition-transform"
                :class="reportingExpanded ? 'pi-chevron-down' : 'pi-chevron-right'"
              />
            </div>

            <!-- Submenu items -->
            <ul
              v-if="reportingExpanded && showLabels"
              class="list-none p-0 m-0 ml-4"
            >
              <li>
                <a
                  class="flex flex-row items-center cursor-pointer p-3 pl-8 border-l-2 duration-150 transition-colors text-sm"
                  :class="[
                    isActiveRoute('/admin/emails')
                      ? 'text-primary-emphasis border-primary-emphasis'
                      : 'text-surface-600 dark:text-surface-200 border-transparent hover:border-surface-300 dark:hover:border-surface-500',
                  ]"
                  @click="handleNavigation('/admin/emails')"
                >
                  <i class="pi pi-envelope mr-2 !text-sm" />
                  <span class="font-medium">Emails</span>
                </a>
              </li>
              <li>
                <a
                  class="flex flex-row items-center cursor-pointer p-3 pl-8 border-l-2 duration-150 transition-colors text-sm"
                  :class="[
                    isActiveRoute('/admin/reporting/lifetimers')
                      ? 'text-primary-emphasis border-primary-emphasis'
                      : 'text-surface-600 dark:text-surface-200 border-transparent hover:border-surface-300 dark:hover:border-surface-500',
                  ]"
                  @click="handleNavigation('/admin/reporting/lifetimers')"
                >
                  <i class="pi pi-star mr-2 !text-sm" />
                  <span class="font-medium">Lifetimers</span>
                </a>
              </li>
              <li>
                <a
                  class="flex flex-row items-center cursor-pointer p-3 pl-8 border-l-2 duration-150 transition-colors text-sm"
                  :class="[
                    isActiveRoute('/admin/reporting/vip-passes')
                      ? 'text-primary-emphasis border-primary-emphasis'
                      : 'text-surface-600 dark:text-surface-200 border-transparent hover:border-surface-300 dark:hover:border-surface-500',
                  ]"
                  @click="handleNavigation('/admin/reporting/vip-passes')"
                >
                  <i class="pi pi-ticket mr-2 !text-sm" />
                  <span class="font-medium">VIP Passes</span>
                </a>
              </li>
              <li>
                <a
                  class="flex flex-row items-center cursor-pointer p-3 pl-8 border-l-2 duration-150 transition-colors text-sm"
                  :class="[
                    isActiveRoute('/admin/reporting/checked-in')
                      ? 'text-primary-emphasis border-primary-emphasis'
                      : 'text-surface-600 dark:text-surface-200 border-transparent hover:border-surface-300 dark:hover:border-surface-500',
                  ]"
                  @click="handleNavigation('/admin/reporting/checked-in')"
                >
                  <i class="pi pi-check-circle mr-2 !text-sm" />
                  <span class="font-medium">Checked In</span>
                </a>
              </li>
              <li>
                <a
                  class="flex flex-row items-center cursor-pointer p-3 pl-8 border-l-2 duration-150 transition-colors text-sm text-surface-600 dark:text-surface-200 border-transparent hover:border-surface-300 dark:hover:border-surface-500"
                  href="https://lookerstudio.google.com/s/gpYIkrk-SJU"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i class="pi pi-chart-line mr-2 !text-sm" />
                  <span class="font-medium">Analytics</span>
                </a>
              </li>
            </ul>
          </li>

          <li v-if="isAdmin">
            <a
              v-tooltip.right="!showLabels ? { value: 'Tracking', showOnlyWhenTruncated: false, class: 'tooltip-custom' } : null"
              class="flex flex-row items-center cursor-pointer p-4 border-l-2 duration-150 transition-colors"
              :class="[
                isActiveRoute('/tracking')
                  ? 'text-primary-emphasis border-primary-emphasis'
                  : 'text-surface-600 dark:text-surface-200 border-transparent hover:border-surface-300 dark:hover:border-surface-500',
                showLabels ? 'justify-start' : 'justify-center',
              ]"
              @click="handleNavigation('/tracking')"
            >
              <i
                class="pi pi-map !text-base lg:!text-xl !leading-none"
                :class="showLabels ? 'mr-3' : ''"
              />
              <span
                v-if="showLabels"
                class="font-medium text-base"
              >Tracking</span>
            </a>
          </li>

          <li v-if="isCheckinVolunteer">
            <a
              v-tooltip.right="!showLabels ? { value: 'Check People In', showOnlyWhenTruncated: false, class: 'tooltip-custom' } : null"
              class="flex flex-row items-center cursor-pointer p-4 border-l-2 duration-150 transition-colors"
              :class="[
                isActiveRoute('/checkin')
                  ? 'text-primary-emphasis border-primary-emphasis'
                  : 'text-surface-600 dark:text-surface-200 border-transparent hover:border-surface-300 dark:hover:border-surface-500',
                showLabels ? 'justify-start' : 'justify-center',
              ]"
              @click="handleNavigation('/checkin')"
            >
              <i
                class="pi pi-check-circle !text-base lg:!text-xl !leading-none"
                :class="showLabels ? 'mr-3' : ''"
              />
              <span
                v-if="showLabels"
                class="font-medium text-base"
              >Check People In</span>
            </a>
          </li>

          <!-- Simple Lineup link for regular members (no submenu) -->
          <li v-if="showLineupMenu && !hasLineupSubmenu">
            <a
              v-tooltip.right="!showLabels ? { value: 'Lineup', showOnlyWhenTruncated: false, class: 'tooltip-custom' } : null"
              class="flex flex-row items-center cursor-pointer p-4 border-l-2 duration-150 transition-colors"
              :class="[
                isActiveRoute('/lineup')
                  ? 'text-primary-emphasis border-primary-emphasis'
                  : 'text-surface-600 dark:text-surface-200 border-transparent hover:border-surface-300 dark:hover:border-surface-500',
                showLabels ? 'justify-start' : 'justify-center',
              ]"
              @click="handleNavigation('/lineup')"
            >
              <i
                class="pi pi-map-marker !text-base lg:!text-xl !leading-none"
                :class="showLabels ? 'mr-3' : ''"
              />
              <span
                v-if="showLabels"
                class="font-medium text-base"
              >Lineup</span>
            </a>
          </li>
        </ul>

        <div class="lg:mt-auto">
          <hr class="mx-4 border-t border-0 border-surface" />

          <!-- Added Notifications link (mobile only) -->
        <a
          v-tooltip.right="!showLabels ? { value: 'Notifications', showOnlyWhenTruncated: false, class: 'tooltip-custom' } : null"
          class="lg:hidden flex flex-row items-center cursor-pointer p-4 text-surface-600 dark:text-surface-200 border-l-2 border-transparent hover:border-surface-300 dark:hover:border-surface-500 duration-150 transition-colors"
          :class="[
            isActiveRoute('/notifications')
              ? 'text-primary-emphasis border-primary-emphasis'
              : '',
            showLabels ? 'justify-start' : 'justify-center',
          ]"
          @click="handleNavigation('/notifications')"
        >
          <div class="relative">
            <i
              class="pi pi-bell text-base leading-none"
              :class="showLabels ? 'mr-2' : ''"
            />
            <span
              v-if="unreadCount > 0"
              class="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"
              aria-label="Unread notifications"
            />
          </div>
          <span
            v-if="showLabels"
            class="font-medium"
          >Notifications</span>
        </a>

        <!-- Added Logout link (mobile only) -->
        <a
          class="lg:hidden flex flex-row items-center cursor-pointer p-4 text-surface-600 dark:text-surface-200 border-l-2 border-transparent hover:border-surface-300 dark:hover:border-surface-500 duration-150 transition-colors"
          :class="showLabels ? 'justify-start' : 'justify-center'"
          @click="handleLogout"
        >
          <i
            class="pi pi-sign-out text-base leading-none"
            :class="showLabels ? 'mr-2' : ''"
          />
          <span
            v-if="showLabels"
            class="font-medium"
          >Sign Out</span>
        </a>

        <!-- Support link -->
        <a
          v-tooltip.right="!showLabels ? { value: 'Support', showOnlyWhenTruncated: false, class: 'tooltip-custom' } : null"
          class="flex flex-row items-center cursor-pointer p-4 text-surface-600 dark:text-surface-200 border-l-2 border-transparent hover:border-surface-300 dark:hover:border-surface-500 duration-150 transition-colors"
          :class="[
            isActiveRoute('/support')
              ? 'text-primary-emphasis border-primary-emphasis'
              : '',
            showLabels ? 'justify-start' : 'justify-center',
          ]"
          @click="handleNavigation('/support')"
        >
          <i
            class="pi pi-question-circle !text-base lg:!text-xl !leading-none"
            :class="showLabels ? 'mr-3' : ''"
          />
          <span
            v-if="showLabels"
            class="font-medium text-base"
          >Support</span>
        </a>

        <a
          v-tooltip.right="!showLabels ? { value: isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode', showOnlyWhenTruncated: false, class: 'tooltip-custom' } : null"
          class="my-4 flex flex-row items-center cursor-pointer p-4 text-surface-600 dark:text-surface-200 border-l-2 border-transparent hover:border-surface-300 dark:hover:border-surface-500 duration-150 transition-colors"
          :class="showLabels ? 'justify-start' : 'justify-center'"
          @click="toggleDarkMode"
        >
          <i
            :class="[
              'text-base lg:!text-xl leading-none text-primary',
              showLabels ? 'mr-3' : '',
              isDarkMode ? 'pi pi-sun' : 'pi pi-moon',
            ]"
          />
          <span
            v-if="showLabels"
            class="font-medium"
          >{{ props.isDarkMode ? 'Light' : 'Dark' }} Mode</span>
        </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDrawer } from '~/composables/useDrawer'
import { useNotifications } from '~/composables/useNotifications'

const props = defineProps({
  isAdmin: {
    type: Boolean,
    default: false,
  },
  isBandAdmin: {
    type: Boolean,
    default: false,
  },
  isRedshirtAdmin: {
    type: Boolean,
    default: false,
  },
  isRedshirtLieutenant: {
    type: Boolean,
    default: false,
  },
  isCheckinVolunteer: {
    type: Boolean,
    default: false,
  },
  isDarkMode: {
    type: Boolean,
    default: false,
  },
  hasSubkrewe: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['toggleDarkMode'])
const isDesktop = ref(false)
const isCollapsed = ref(false)
const lineupExpanded = ref(false)
const reportingExpanded = ref(false)
const inventoryExpanded = ref(false)
const router = useRouter()

// Import notifications functionality
const { unreadCount } = useNotifications()

// Get drawer functionality from composable
const { closeDrawer } = useDrawer()

// Toggle sidebar collapse state
const toggleCollapse = () => {
  isCollapsed.value = !isCollapsed.value
  // Persist preference
  localStorage.setItem('sidebarCollapsed', isCollapsed.value ? 'true' : 'false')
}

// Computed for showing labels (expanded desktop or mobile drawer)
const showLabels = computed(() => !isCollapsed.value || !isDesktop.value)

// Handle logout functionality
const handleLogout = async () => {
  const { signOutUser } = useAuth()
  await signOutUser()
}

// Check if the viewport width is desktop-sized (lg breakpoint - 1024px or more)
const checkIfDesktop = () => {
  isDesktop.value = window.innerWidth >= 1024
}

onMounted(() => {
  checkIfDesktop()
  window.addEventListener('resize', checkIfDesktop)
  // Restore collapsed state from localStorage
  const savedCollapsed = localStorage.getItem('sidebarCollapsed')
  if (savedCollapsed === 'true') {
    isCollapsed.value = true
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', checkIfDesktop)
})

const route = useRoute()
const isActiveRoute = (path: string) => route.path === path

// Lineup menu access control
const canManageLineup = computed(() => props.isAdmin || props.isRedshirtAdmin)
const canViewLineup = computed(() => true) // All users can view
const canCheckInLineup = computed(() => props.isAdmin || props.isRedshirtAdmin || props.isRedshirtLieutenant)
const canViewRadioList = computed(() => props.isAdmin || props.isRedshirtAdmin || props.isRedshirtLieutenant)

// Show lineup menu only if user has at least one access level
const showLineupMenu = computed(() => canManageLineup.value || canViewLineup.value || canCheckInLineup.value)

// Show submenu if user has access to more than one lineup page
const hasLineupSubmenu = computed(() => {
  const accessCount = [canManageLineup.value, canViewLineup.value, canCheckInLineup.value, canViewRadioList.value].filter(Boolean).length
  return accessCount > 1
})

// Check if any lineup route is active
const isLineupActive = computed(() => {
  return route.path === '/lineup' || route.path === '/admin/lineup' || route.path === '/admin/lineup-status' || route.path === '/admin/radio-list'
})

// Check if any reporting route is active (includes emails)
const isReportingActive = computed(() => {
  return route.path.startsWith('/admin/reporting') || route.path === '/admin/emails'
})

// Check if any inventory route is active
const isInventoryActive = computed(() => {
  return route.path.startsWith('/admin/inventory') || route.path === '/checkin/equipment'
})

// Show admin section if user has any admin-like role
const showAdminSection = computed(() => {
  return props.isAdmin || props.isBandAdmin || props.isRedshirtAdmin || props.isCheckinVolunteer
})

// Auto-expand submenu if on a lineup route
watch(() => route.path, (newPath) => {
  if (newPath === '/lineup' || newPath === '/admin/lineup' || newPath === '/admin/lineup-status' || newPath === '/admin/radio-list') {
    lineupExpanded.value = true
  }
  if (newPath.startsWith('/admin/reporting') || newPath === '/admin/emails') {
    reportingExpanded.value = true
  }
  if (newPath.startsWith('/admin/inventory') || newPath === '/checkin/equipment') {
    inventoryExpanded.value = true
  }
}, { immediate: true })

const toggleDarkMode = () => {
  emit('toggleDarkMode')
}

// Update navigation helper to also close the drawer
const handleNavigation = (path: string) => {
  // Close the drawer before navigating
  closeDrawer()

  // Continue with navigation
  router.push(path)
}
</script>

<style>
/* Custom tooltip styling */
.tooltip-custom {
  background-color: var(--surface-900);
  color: var(--surface-0);
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.p-tooltip.p-tooltip-right .p-tooltip-arrow {
  border-right-color: var(--surface-900);
}

/* Dark mode styling */
:root.dark .tooltip-custom {
  background-color: var(--surface-700);
  color: var(--surface-0);
}

:root.dark .p-tooltip.p-tooltip-right .p-tooltip-arrow {
  border-right-color: var(--surface-700);
}
</style>
